import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth.service';
import { AwsService } from 'src/app/services/aws.service';
import { DataService } from 'src/app/services/data.service';
import { GetTranslationService } from 'src/app/services/get-translation.service';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { MenuTagModel } from 'src/app/models/menu-tag.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.scss'],
})
export class MyProfileComponent implements OnInit {
  @Input() type: 'account' | 'preferences';

  photo: string = null;
  lang = localStorage.getItem('userLang') || 'tr';
  photoUploading = false;
  unique: number = new Date().getTime();
  objectURL: string;

  showAlergens = true;
  tags: MenuTagModel[] = [];
  newAlergen: MenuTagModel = new MenuTagModel();

  privacyPolicyText: string;
  termsAndConditionsText: string;

  constructor(
    private mdlCtrl: ModalController,
    public dataServ: DataService,
    public authServ: AuthService,
    private alertCtrl: AlertController,
    private translation: GetTranslationService,
    private awsServ: AwsService,
    private platform: Platform,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await this.authServ.getMe();
    if (this.authServ.profile.profile_pic_url.length !== 0) {
      this.objectURL = await this.awsServ.getProfilePhoto(
        this.authServ.profile.id
      );
    }

    this.privacyPolicyText =
      this.dataServ.rData.register_by_module_page_field_setup.find(
        (field) => field.control_part_value === 'privacy_policy'
      )?.control_part_value_ml;
    this.termsAndConditionsText =
      this.dataServ.rData.register_by_module_page_field_setup.find(
        (field) => field.control_part_value === 'terms_and_conditions'
      )?.control_part_value_ml;

    this.getTags();
  }

  async close() {
    await this.mdlCtrl.dismiss();
  }

  async getTags() {
    this.tags = await this.dataServ.getMenuTags(
      this.authServ.profile.id,
      'profile'
    );
    this.changeDetectorRef.detectChanges();
  }

  async deleteTag(tag: MenuTagModel) {
    await this.dataServ.deleteTagData(tag.id);
    this.getTags();
  }

  async addAlergen() {
    this.newAlergen.menu_tag_category_name = 'Dietary Restrictions';
    this.newAlergen.table_name = 'profile';
    this.newAlergen.table_record_id = this.authServ.profile.id;
    this.newAlergen.type = 'Profile Tags';
    this.newAlergen.hotel_id = this.authServ.profile.hotel_id;

    await this.dataServ.postTagData(this.newAlergen);
    this.newAlergen = new MenuTagModel();
    this.getTags();
  }

  uploadPhoto(file: File) {
    if (file) {
      this.photoUploading = true;
      const READER = new FileReader();
      READER.onload = async () => {
        this.photo = READER.result as string;

        const RESP = await this.awsServ.uploadProfileImage(
          this.photo,
          this.authServ.profile.id
        );

        this.authServ.profile.profile_pic_url = RESP.link;

        this.authServ.putMe(this.authServ.profile);
        this.unique = new Date().getTime();
        this.photoUploading = false;
      };

      READER.readAsDataURL(file);
    }
  }

  async takePicture(inputEl: HTMLInputElement) {
    if (this.platform.is('capacitor')) {
      {
        const STATUS = await Camera.checkPermissions();

        if (STATUS.camera !== 'granted') {
          await Camera.requestPermissions();
        }

        const image = await Camera.getPhoto({
          quality: 50,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          presentationStyle: 'popover',
          saveToGallery: true,
          source: CameraSource.Prompt,
        });

        this.photoUploading = true;

        this.photo = image.dataUrl;

        const RESP = await this.awsServ.uploadProfileImage(
          this.photo,
          this.authServ.profile.id
        );

        this.authServ.profile.profile_pic_url = RESP.link;

        this.authServ.putMe(this.authServ.profile);
        this.unique = new Date().getTime();
        this.photoUploading = false;
      }
    } else {
      inputEl.click();
    }
  }

  async deleteAccount() {
    const MSG = await this.translation.getTranslate(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );
    const YES = await this.translation.getTranslate('Yes');
    const NO = await this.translation.getTranslate('No');

    const ALERT = await this.alertCtrl.create({
      message: MSG,
      buttons: [
        {
          text: NO,
          role: NO,
        },
        {
          text: YES,
          role: YES,
          handler: async () => {
            await this.authServ.deleteMe();
            this.close();
          },
        },
      ],
    });

    await ALERT.present();
  }

  getOptions(optionJson: string): string[] {
    try {
      const options = JSON.parse(optionJson);
      return (options[this.lang] || options['tr'] || '')
        .split('|')
        .map((o) => o.trim())
        .filter((o) => o);
    } catch {
      return [];
    }
  }

  convertNumberFieldsToString() {
    if (this.authServ.profile && this.authServ.profile.my_preferences) {
      this.authServ.profile.my_preferences.forEach((field) => {
        if (field.field_type === 6 && typeof field.field_value === 'number') {
          field.field_value = (field.field_value as number).toString();
        }
        if (field.field_type === 5 && typeof field.field_value === 'boolean') {
          field.field_value = (field.field_value as boolean).toString();
        }
      });
    }
  }

  async save() {
    this.convertNumberFieldsToString();
    try {
      await this.authServ.putMe(this.authServ.profile);
      this.close();
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  }
}
